// @flow weak

import { width } from "@mui/system"
import { useRef } from "react"
import { Matrix } from "transformation-matrix-js"

const getDefaultMat = () => Matrix.from(1, 0, 0, 1, -5, -5)

export default ({
  onMouseLeave,
  canvasEl,
  changeMat,
  changeDragging,
  zoomStart,
  zoomEnd,
  changeZoomStart,
  changeZoomEnd,
  layoutParams,
  zoomWithPrimary,
  dragWithPrimary,
  mat,
  onMouseMove,
  onMouseUp,
  onMouseDown,
  onClick,
  dragging,
  allowZoom,
  allowPanning,
  creatable
}) => {
  const mousePosition = useRef({ x: 0, y: 0 })
  const prevMousePosition = useRef({ x: 0, y: 0 })
  var rightMarginPanning = creatable ? 150 : 90 // to keep right margin for the severity popup
  var bottomMarginPanning = creatable ? 220 : 80 // to keep bottom margin for the severity popup
  const zoomIn = (direction, point) => {
    const [mx, my] = [point.x, point.y]
    const { iw, ih } = layoutParams.current
    const { height, width } = canvasEl.current.getBoundingClientRect()

    let zoomIntensity = 0.03 // speed of zooming
    let scale =
      typeof direction === "object" ? direction.to / mat.a : 1 + zoomIntensity * direction

    // NOTE: We're mutating mat here
    let maxPanX = iw - (width * mat.a) + (rightMarginPanning * mat.a) // to keep padding for the severity popup
    let maxPanY = ih - (ih * mat.a) + (bottomMarginPanning * mat.a) // to keep padding for the severity popup
    mat.translate(mx, my).scaleU(scale)
    if (mat.a > 1) mat.scaleU(1 / mat.a) // minimum zooming scale
    if (mat.a < 0.18) mat.scaleU(0.18 / mat.a) //maximum zooming scale
    mat.translate(-mx, -my)
    mat.e = mat.a == 1 ? -5 : mat.e < -5 || maxPanX < -5 ? -5 : mat.e > maxPanX ? maxPanX : mat.e
    mat.f = mat.a == 1 ? -5 : mat.f < -5 ? -5 : mat.f > maxPanY ? maxPanY : mat.f
    changeMat(mat.clone())
  }

  const mouseEvents = {
    // onMouseEnter: (e) => {
    //   return changeDragging(false) // To disable dragging when cursor is in the canvas
    // },
    onMouseOut: (e) => {
      return changeDragging(false) // To disable dragging when cursor is out of the canvas
    },
    onClick: (e) => {
      e.preventDefault()
      const projMouse = mat.applyToPoint(
        mousePosition.current.x,
        mousePosition.current.y
      )
      if (zoomWithPrimary && e.button === 0) {
        changeZoomStart(projMouse)
        changeZoomEnd(projMouse)
        return
      }
      if (e.button === 0) {

        const { iw, ih } = layoutParams.current
        onClick({ x: projMouse.x / iw, y: projMouse.y / ih })
      }
    },
    onMouseMove: (e) => {
      const { left, top, height, width } = canvasEl.current.getBoundingClientRect()
      prevMousePosition.current.x = mousePosition.current.x
      prevMousePosition.current.y = mousePosition.current.y
      mousePosition.current.x = e.clientX - left
      mousePosition.current.y = e.clientY - top
      const projMouse = mat.applyToPoint(
        mousePosition.current.x,
        mousePosition.current.y
      )

      if (zoomWithPrimary && zoomStart) {
        changeZoomEnd(projMouse)
      }

      const { iw, ih } = layoutParams.current
      onMouseMove({ x: projMouse.x / iw, y: projMouse.y / ih })
      if (dragging && allowPanning) {
        mat.translate(
          prevMousePosition.current.x - mousePosition.current.x,
          prevMousePosition.current.y - mousePosition.current.y
        )

        let maxPanX = (width * mat.a) - iw - (rightMarginPanning * mat.a) <= mat.e ? iw - (width * mat.a) + (rightMarginPanning * mat.a) : iw - (iw * mat.a) // to keep padding for the severity popup
        let maxPanY = ih - (ih * mat.a) + (bottomMarginPanning * mat.a) // to keep padding for the severity popup

        // to check while dragging, image should not completely go out of the canvas
        mat.e = mat.a == 1 || mat.e < -5 || (width * mat.a) - iw > mat.e ? -5 : mat.e > maxPanX ? maxPanX : mat.e // for x position
        mat.f = mat.f < -5 ? -5 : mat.f > maxPanY ? maxPanY : mat.f // for y position
        changeMat(mat.clone())
      }
      e.preventDefault()
    },
    onMouseDown: (e, specialEvent = {}) => {
      e.preventDefault()

      if (
        e.button === 1 ||
        e.button === 2 ||
        (e.button === 0 && dragWithPrimary && allowPanning)
      )
        return changeDragging(true)

      const projMouse = mat.applyToPoint(
        mousePosition.current.x,
        mousePosition.current.y
      )
      if (zoomWithPrimary && e.button === 0) {
        changeZoomStart(projMouse)
        changeZoomEnd(projMouse)
        return
      }
      if (e.button === 0) {
        if (specialEvent.type === "resize-box") {
          // onResizeBox()
        }
        if (specialEvent.type === "move-region") {
          // onResizeBox()
        }
        const { iw, ih } = layoutParams.current
        onMouseDown({ x: projMouse.x / iw, y: projMouse.y / ih })
      }
    },
    onMouseUp: (e) => {
      const { height, width } = canvasEl.current.getBoundingClientRect()
      e.preventDefault()
      const projMouse = mat.applyToPoint(
        mousePosition.current.x,
        mousePosition.current.y
      )
      if (zoomStart && allowZoom) {
        const zoomEnd = projMouse
        if (
          Math.abs(zoomStart.x - zoomEnd.x) < 10 &&
          Math.abs(zoomStart.y - zoomEnd.y) < 10
        ) {
          if (mat.a < 1) {
            zoomIn({ to: 1 }, mousePosition.current)
          } else {
            zoomIn({ to: 0.25 }, mousePosition.current)
          }
        } else {
          const { iw, ih } = layoutParams.current

          if (zoomStart.x > zoomEnd.x) {
            ;[zoomStart.x, zoomEnd.x] = [zoomEnd.x, zoomStart.x]
          }
          if (zoomStart.y > zoomEnd.y) {
            ;[zoomStart.y, zoomEnd.y] = [zoomEnd.y, zoomStart.y]
          }

          // The region defined by zoomStart and zoomEnd should be the new transform
          let scale = Math.min(
            (zoomEnd.x - zoomStart.x) / iw,
            (zoomEnd.y - zoomStart.y) / ih
          )
          if (scale < 0.05) scale = 0.05
          if (scale > 10) scale = 10

          const newMat = getDefaultMat()
            .translate(zoomStart.x, zoomStart.y)
            .scaleU(scale)

          let maxPanX = (width * newMat.a) - iw - (rightMarginPanning * newMat.a) <= newMat.e ? iw - (width * newMat.a) + (rightMarginPanning * newMat.a) : iw - (iw * newMat.a) // to keep padding for the severity popup
          let maxPanY = ih - (ih * newMat.a) + (bottomMarginPanning * newMat.a) // to keep padding for the severity popup
          newMat.e = newMat.a == 1 || newMat.e < -5 || (width * newMat.a) - iw > newMat.e ? -5 : newMat.e > maxPanX ? maxPanX : newMat.e
          newMat.f = newMat.f < -5 ? -5 : newMat.f > maxPanY ? maxPanY : newMat.f
          changeMat(newMat.clone())
        }

        changeZoomStart(null)
        changeZoomEnd(null)
      }
      
      if (e.button === 0) {
        const { iw, ih } = layoutParams.current
        onMouseUp({ x: projMouse.x / iw, y: projMouse.y / ih })
      }
      return changeDragging(false)
    },
    onWheel: (e) => {
      const direction = e.deltaY > 0 ? 1 : e.deltaY < 0 ? -1 : 0
      allowZoom && zoomIn(direction, mousePosition.current)
      // e.preventDefault()
    },
    onContextMenu: (e) => {
      e.preventDefault()
    },
  }
  return { mouseEvents, mousePosition }
}
